import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import featuredContentService from '../../../services/featuredContentService';

const FeaturedContentSettings = () => {
  const { user } = useAuth();
  const [featuredItems, setFeaturedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Load featured content
  useEffect(() => {
    loadFeaturedContent();
  }, []);

  const loadFeaturedContent = async () => {
    setLoading(true);
    try {
      const { data, error } = await featuredContentService?.getAdminFeaturedContent();
      if (error) throw new Error(error);
      setFeaturedItems(data || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async (formData) => {
    try {
      const { data, error } = await featuredContentService?.createFeaturedContent({
        ...formData,
        created_by: user?.id,
        display_order: featuredItems?.length + 1
      });

      if (error) throw new Error(error);
      
      await loadFeaturedContent();
      setShowCreateForm(false);
      setError(null);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleUpdateItem = async (id, formData) => {
    try {
      const { data, error } = await featuredContentService?.updateFeaturedContent(id, formData);
      if (error) throw new Error(error);
      
      await loadFeaturedContent();
      setEditingItem(null);
      setError(null);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!confirm('Are you sure you want to delete this featured content?')) return;

    try {
      const { error } = await featuredContentService?.deleteFeaturedContent(id);
      if (error) throw new Error(error);
      
      await loadFeaturedContent();
      setError(null);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleImageUpload = async (file, itemId = null) => {
    setUploadingImage(true);
    try {
      const filename = `featured-${Date.now()}`;
      const { data, error } = await featuredContentService?.uploadFeaturedImage(file, filename);
      
      if (error) throw new Error(error);
      
      return data?.path;
    } catch (err) {
      setError(err?.message);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleReorder = async (dragIndex, hoverIndex) => {
    const reorderedItems = [...featuredItems];
    const draggedItem = reorderedItems?.[dragIndex];
    
    reorderedItems?.splice(dragIndex, 1);
    reorderedItems?.splice(hoverIndex, 0, draggedItem);
    
    setFeaturedItems(reorderedItems);

    try {
      const { error } = await featuredContentService?.reorderFeaturedContent(reorderedItems);
      if (error) throw new Error(error);
    } catch (err) {
      setError(err?.message);
      await loadFeaturedContent(); // Reload on error
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="flex items-center space-x-3 text-muted-foreground">
          <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          <span>Loading featured content...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-foreground">Featured Content Management</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Manage hero section background images and text content
          </p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2"
        >
          Add Content
        </Button>
      </div>
      {/* Error Alert */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg"
        >
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" className="w-5 h-5 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-destructive hover:text-destructive/80"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </motion.div>
      )}
      {/* Featured Items List */}
      <div className="space-y-4">
        <AnimatePresence>
          {featuredItems?.map((item, index) => (
            <FeaturedContentItem
              key={item?.id}
              item={item}
              index={index}
              isEditing={editingItem === item?.id}
              onEdit={() => setEditingItem(item?.id)}
              onCancelEdit={() => setEditingItem(null)}
              onUpdate={(formData) => handleUpdateItem(item?.id, formData)}
              onDelete={() => handleDeleteItem(item?.id)}
              onImageUpload={handleImageUpload}
              onReorder={handleReorder}
              uploadingImage={uploadingImage}
            />
          ))}
        </AnimatePresence>

        {featuredItems?.length === 0 && (
          <div className="text-center py-12 bg-muted/20 rounded-lg border-2 border-dashed border-muted">
            <Icon name="Image" className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Featured Content</h4>
            <p className="text-muted-foreground mb-4">
              Add your first featured content item to get started
            </p>
            <Button
              variant="outline"
              iconName="Plus"
              onClick={() => setShowCreateForm(true)}
            >
              Create Featured Content
            </Button>
          </div>
        )}
      </div>
      {/* Create Form Modal */}
      <AnimatePresence>
        {showCreateForm && (
          <CreateFeaturedContentModal
            onSubmit={handleCreateItem}
            onCancel={() => setShowCreateForm(false)}
            onImageUpload={handleImageUpload}
            uploadingImage={uploadingImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Featured Content Item Component
const FeaturedContentItem = ({
  item,
  index,
  isEditing,
  onEdit,
  onCancelEdit,
  onUpdate,
  onDelete,
  onImageUpload,
  onReorder,
  uploadingImage
}) => {
  const [formData, setFormData] = useState({
    title: item?.title || '',
    subtitle: item?.subtitle || '',
    is_active: item?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    onUpdate(formData);
  };

  const handleFileChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const imagePath = await onImageUpload(file);
    if (imagePath) {
      setFormData(prev => ({ ...prev, image_path: imagePath }));
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-card border border-border rounded-lg p-6"
    >
      {isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Title"
              value={formData?.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
              placeholder="Enter featured title..."
              required
            />
            <Input
              label="Subtitle"
              value={formData?.subtitle}
              onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e?.target?.value }))}
              placeholder="Enter featured subtitle..."
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Background Image
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-muted file:text-foreground hover:file:bg-muted/80"
                disabled={uploadingImage}
              />
              {uploadingImage && (
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <div className="w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                  <span>Uploading...</span>
                </div>
              )}
            </div>
            {item?.imageUrl && (
              <div className="mt-2">
                <img
                  src={item?.imageUrl}
                  alt="Current background"
                  className="w-32 h-20 object-cover rounded-lg border"
                />
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData?.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e?.target?.checked }))}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
              />
              <span className="text-sm text-foreground">Active</span>
            </label>
          </div>

          <div className="flex items-center space-x-3 pt-4 border-t">
            <Button type="submit" size="sm" iconName="Check">
              Save Changes
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconName="X"
              onClick={onCancelEdit}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex items-start space-x-4">
          {item?.imageUrl && (
            <div className="flex-shrink-0">
              <img
                src={item?.imageUrl}
                alt="Featured background"
                className="w-24 h-16 object-cover rounded-lg border"
              />
            </div>
          )}
          
          <div className="flex-grow min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-semibold text-foreground truncate">{item?.title}</h4>
                <p className="text-sm text-muted-foreground mt-1">{item?.subtitle}</p>
                <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center space-x-1">
                    <Icon name="Hash" size={12} />
                    <span>Order: {item?.display_order}</span>
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    item?.is_active 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                  }`}>
                    {item?.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="flex items-center space-x-2 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Edit"
                  onClick={onEdit}
                  className="h-8 w-8 p-0"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={onDelete}
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Create Modal Component
const CreateFeaturedContentModal = ({ onSubmit, onCancel, onImageUpload, uploadingImage }) => {
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image_path: '',
    is_active: true
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(formData);
  };

  const handleFileChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const imagePath = await onImageUpload(file);
    if (imagePath) {
      setFormData(prev => ({ ...prev, image_path: imagePath }));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e?.target === e?.currentTarget && onCancel()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-card border border-border rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-foreground">Create Featured Content</h3>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onCancel}
            className="h-8 w-8 p-0"
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Title"
              value={formData?.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
              placeholder="Enter featured title..."
              required
            />
            <Input
              label="Subtitle"
              value={formData?.subtitle}
              onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e?.target?.value }))}
              placeholder="Enter featured subtitle..."
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Background Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-muted file:text-foreground hover:file:bg-muted/80"
              disabled={uploadingImage}
            />
            {uploadingImage && (
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <div className="w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                <span>Uploading image...</span>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData?.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e?.target?.checked }))}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
              />
              <span className="text-sm text-foreground">Active</span>
            </label>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              iconName="Plus"
              disabled={uploadingImage}
            >
              Create Content
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default FeaturedContentSettings;